import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import ServiceDeliveryPlan from './ServiceDeliveryPlan';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  createConsent,
  createConsentSignature,
  IfspConsentSignatureType,
  IfspSDPAndConsentType,
  ServiceDeliveryType,
  setIfspSDPAndConsent,
  updateIfspSDPAndConsent,
} from '../../../redux/features/ifsp/ifspSDPAndConsentSlice';
import {
  addOrUpdateIfspServiceDeliveryPlanAndConsent,
  downloadIfspSDPAndConsent,
  getIfsp,
  getIfspServiceDeliveryPlanAndConsent,
} from '../../../services/ifspClient/IfspApi';
import IfspConsent from './IfspConsent';
import MediumTypography from '../../../components/formlib/MediumTypography';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { AlertColor } from '@mui/material';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import ModalPopup from '../../../components/formlib/ModalPopup';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import dayjs from 'dayjs';
import IfspHeld from '../ifspComponents/IfspHeld';
import IfspUpdateModalPopup from '../../ifsp/IfspUpdateModalPopup';
import { ReactComponent as Lock } from '../../../assets/images/locked.svg';
import { useSDPAndConsentValidationCheck } from './sDPAndConsentsComponents/ifspSDPAndConsentValidationCheck';
import { formatName } from '../../../utils/nameUtils';
import { ReactComponent as DownloadIcon } from '../../../assets/images/downloadIcon.svg';
import { ApiError, isCustomError } from '../../../services/ApiResponseHandler';
import {
  setIfspClientDetails,
  updateIfspClientDetails,
} from '../../../redux/features/ifsp/ifspClientDetailsSlice';
import { getClientDocumentFileName } from '../../../utils/fileUtils';

export const IfspSDPAndConsentMainScreen = () => {
  const dispatch = useAppDispatch();
  const ifspSdpAndConsentDetails = useAppSelector(
    (state) => state.ifspSDPAndConsent.sdpAndConsent,
  );
  const ifspSdpAndConsentOriginalDetails = useAppSelector(
    (state) => state.ifspSDPAndConsent.originalSdpAndConsent,
  );
  const ifspHeatBeatDetails = useAppSelector(
    (state) => state.ifspHeatBeatDetails,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const [openUpdateModal, setOpenUpdateModal] = useState<boolean>(false);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [transitionRenderKey, setTransitionRenderKey] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [isUnsavedPopupConfirmed, setIsUnsavedPopupConfirmed] =
    useState<boolean>(false);
  const [
    isAnyServicesChangedWithoutIfspLockModal,
    setIsAnyServicesChangedWithoutIfspLockModal,
  ] = useState<boolean>(false);
  const [isInsuranceConsentMissingModal, setIsInsuranceConsentMissingModal] =
    useState<boolean>(false);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { isSDPAndConsentChnaged, isAnyServicesChangedWithoutIfspLock } =
    useIfspUnsavedChanges();
  const parentRef = useRef<HTMLDivElement>(null);

  const validationError = useSDPAndConsentValidationCheck(
    ifspSdpAndConsentDetails,
  );
  const isSigned = (arr: IfspConsentSignatureType[]) =>
    arr.some(
      (x) =>
        Boolean(x.signature) ||
        Boolean(x.rowKey) ||
        Boolean(x.verbalConsent) ||
        Boolean(x.paperConsent),
    );

  const isInsuranceConsentMissing = (data: IfspSDPAndConsentType) => {
    const latest = data.consent[data.consent.length - 1];
    return !(
      isSigned(latest.consentToAccessInsuranceSignature) ||
      Boolean(
        latest.isConsentSkipped && latest.adminAmendmentSignature?.signature,
      )
    );
  };

  const hasValidConsentToLockIfsp = (data: IfspSDPAndConsentType) => {
    const latest = data.consent[data.consent.length - 1];

    const serviceConsentSigned =
      isSigned(latest.consentForServiceSignature) ||
      Boolean(
        latest.isConsentSkipped && latest.adminAmendmentSignature?.signature,
      );

    const hasAtLeastOneService = data.serviceDelivery.some(
      (x: ServiceDeliveryType) => x.servicePlan.length > 0,
    );

    return serviceConsentSigned && hasAtLeastOneService;
  };

  useEffect(() => {
    toggleLoader(true);
    getIfspServiceDeliveryPlanAndConsent()
      .then((res) => {
        dispatch(
          setIfspSDPAndConsent({
            ...res,
            serviceDelivery: res.serviceDelivery,
            consent: res.consent.map((consent) => ({
              ...consent,
              ifspReviewHistory: null,
              uiVersion: consent.uiVersion || 2,
              consentType: consent.consentType || 'consent',
              latestConsent: false,
              sessionReviewedDate:
                ((!consent.isConsentSkipped &&
                  consent.consentForServiceSignature.every(
                    (sig) =>
                      !sig.signature && !sig.rowKey && !sig.verbalConsent,
                  )) ||
                  (!consent.isConsentSkipped &&
                    consent.consentToAccessInsuranceSignature.every(
                      (sig) =>
                        !sig.signature && !sig.rowKey && !sig.verbalConsent,
                    ))) &&
                !res.lockWithoutConsentSignature
                  ? ''
                  : consent.completedOn,
              consentToAccessInsuranceSignature:
                consent.consentToAccessInsuranceSignature.length > 0
                  ? consent.consentToAccessInsuranceSignature
                  : [createConsentSignature(), createConsentSignature()],
              consentForServiceSignature:
                consent.consentForServiceSignature.length > 0
                  ? consent.consentForServiceSignature
                  : [createConsentSignature(), createConsentSignature()],
            })),
          }),
        );
        toggleLoader(false);
      })
      .catch(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.SDPAndConsent.GetError');
        setToastrDefaultMessage(
          'Failed to get Service Delivery Plan and consent data',
        );
      });
  }, [ifspHeatBeatDetails.serviceDeliveryEmpId]);

  const handleAddOrUpdate = () => {
    if (
      validationError.adminAmendmentSignDate.length > 0 ||
      validationError.adminAmendmentSignature.length > 0 ||
      validationError.assessmentCompletedOn.length > 0 ||
      validationError.reestablishedDate.length > 0 ||
      validationError.consentForServiceSignatureDateOne.length > 0 ||
      validationError.consentForServiceSignatureDateTwo.length > 0 ||
      validationError.consentToAccessInsuranceSignatureDateOne.length > 0 ||
      validationError.consentToAccessInsuranceSignatureDateTwo.length > 0 ||
      validationError.consentForServiceagreeDisagreeService.length > 0 ||
      validationError.participatedInIfspDevelopment.length > 0 ||
      validationError.consentToAccessInsurance.length > 0 ||
      validationError.consentForServiceNotSigned.length > 0 ||
      validationError.consentToAccessInsuranceNotSigned.length > 0
    ) {
      setIsSaveClicked(true);
      setOpen(true);
      setToastrVariable('error');
      setToastrId('IFSP.SDPAndConsent.SubmitError');
      setToastrDefaultMessage(
        'Please correct the highlighted fields before submitting.',
      );
      setTimeout(() => {
        setOpen(false);
      }, 3000);
    } else if (
      hasValidConsentToLockIfsp(ifspSdpAndConsentDetails) &&
      isInsuranceConsentMissing(ifspSdpAndConsentDetails)
    ) {
      setIsInsuranceConsentMissingModal(true);
    } else if (
      isAnyServicesChangedWithoutIfspLock &&
      !hasValidConsentToLockIfsp(ifspSdpAndConsentDetails)
    ) {
      setIsAnyServicesChangedWithoutIfspLockModal(true);
    } else {
      setIsSaveClicked(false);
      setIsAnyServicesChangedWithoutIfspLockModal(false);
      handleAddOrUpdateAfterChecks();
    }
  };

  const handleAddOrUpdateAfterChecks = () => {
    const lockingUserId = localStorage.getItem('userId');
    const formatedUserName = formatName(
      localStorage.getItem('userFirstName'),
      null,
      localStorage.getItem('userLastName'),
      null,
    );
    const consents = [...ifspSdpAndConsentDetails.consent];
    const lastIndex = consents.length - 1;
    const lastConsent = consents[lastIndex];

    if (
      (lastConsent.consentType === 'consent' ||
        lastConsent.consentType === 'consentForChange') &&
      !lastConsent.isAdminAmendment
    ) {
      consents[lastIndex] = {
        ...lastConsent,
        latestConsent: true,
      };
    }

    if (
      hasValidConsentToLockIfsp(ifspSdpAndConsentDetails) &&
      !lastConsent.completedOn
    ) {
      const allDates = [
        ...(lastConsent.consentToAccessInsuranceSignature?.map(
          (sig) => sig.date,
        ) || []),
        ...(lastConsent.consentForServiceSignature?.map((sig) => sig.date) ||
          []),
      ];

      const validDates = allDates.filter((date) => !!date);

      const parsedDates = validDates
        .map((date) => dayjs(date, 'MM/DD/YYYY'))
        .filter((dateObj) => dateObj.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());

      const highestDate =
        parsedDates.length > 0
          ? parsedDates[parsedDates.length - 1].format('MM/DD/YYYY')
          : null;

      const newDatePart =
        lastConsent.isConsentSkipped && lastConsent.isAdminAmendment
          ? lastConsent.adminAmendmentSignature?.date
          : highestDate;

      consents[lastIndex] = {
        ...lastConsent,
        latestConsent: !lastConsent.isConsentSkipped,
        completedOn:
          lastConsent.completedOn ||
          `${dayjs(newDatePart).format('MM/DD/YYYY')} ${dayjs().format(
            'hh:mm A',
          )}`,
        ifspReviewHistory: {
          id: '',
          reviewedById: lockingUserId ?? '',
          name: formatedUserName ?? '',
          date: dayjs().format('MM/DD/YYYY hh:mm A'),
          discipline: '',
        },
      };
    }

    const updatedBody: IfspSDPAndConsentType = {
      ...ifspSdpAndConsentDetails,
      consent: consents,
      isLocked: hasValidConsentToLockIfsp(ifspSdpAndConsentDetails),
    };
    const clientId = localStorage.getItem('ClientId');
    toggleLoader(true);
    addOrUpdateIfspServiceDeliveryPlanAndConsent(updatedBody)
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('IFSP.SDPAndConsent.SaveSuccess');
        setToastrDefaultMessage(
          'Service Delivery Plan and consent saved successfully',
        );
        return getIfspServiceDeliveryPlanAndConsent()
          .then((res) => {
            dispatch(
              setIfspSDPAndConsent({
                ...res,
                serviceDelivery: res.serviceDelivery,
                consent: res.consent.map((consent) => ({
                  ...consent,
                  ifspReviewHistory: null,
                  sessionReviewedDate:
                    ((!consent.isConsentSkipped &&
                      consent.consentForServiceSignature.every(
                        (sig) =>
                          !sig.signature && !sig.rowKey && !sig.verbalConsent,
                      )) ||
                      (!consent.isConsentSkipped &&
                        consent.consentToAccessInsuranceSignature.every(
                          (sig) =>
                            !sig.signature && !sig.rowKey && !sig.verbalConsent,
                        ))) &&
                    !res.lockWithoutConsentSignature
                      ? ''
                      : consent.completedOn,
                  consentType: consent.consentType || 'consent',
                  latestConsent: false,
                  consentToAccessInsuranceSignature:
                    consent.consentToAccessInsuranceSignature.length > 0
                      ? consent.consentToAccessInsuranceSignature
                      : [createConsentSignature(), createConsentSignature()],
                  consentForServiceSignature:
                    consent.consentForServiceSignature.length > 0
                      ? consent.consentForServiceSignature
                      : [createConsentSignature(), createConsentSignature()],
                })),
              }),
            );

            getIfsp(clientId ?? '')
              .then((resp) => {
                dispatch(setIfspClientDetails(resp));
                dispatch(
                  updateIfspClientDetails({
                    reviewHistory: resp.reviewHistory,
                  }),
                );
              })
              .catch(() => {
                setOpen(true);
                setToastrVariable('error');
                setToastrId('IFSP.Consent.DeleteError');
                setToastrDefaultMessage(
                  'Failed to update IFSP details after deletion',
                );
              });
          })
          .catch(() => {
            setOpen(true);
            setToastrVariable('error');
            setToastrId('IFSP.SDPAndConsent.GetError');
            setToastrDefaultMessage(
              'Failed to get Service Delivery Plan and consent data',
            );
          });
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.SDPAndConsent.SaveError');
        setToastrDefaultMessage(
          'Failed to save Service Delivery Plan and consent',
        );
      })
      .finally(() => {
        setIsAnyServicesChangedWithoutIfspLockModal(false);
        toggleLoader(false);
      });
  };

  const handleDownload = () => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    const localTimezone = dayjs.tz.guess();
    if (clientId) {
      downloadIfspSDPAndConsent(clientId, localTimezone)
        .then((response) => {
          const blob = new Blob([response], { type: 'application/pdf' });

          // Create a Blob URL
          const blobUrl = URL.createObjectURL(blob);

          // Open the Blob URL in a new tab
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.target = '_blank';
          anchor.download = getClientDocumentFileName(
            `Individualized Family Service Plan (IFSP).pdf`,
          ); // Set desired file name here

          // Append anchor to document body
          document.body.appendChild(anchor);

          // Trigger anchor click
          anchor.click();

          window.open(blobUrl, '_blank');

          // Clean up after use
          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(anchor);

          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('ConsentForms.downloadErrorMsg');
            setToastrDefaultMessage('Failed to download');
          }
        });
    }
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const ifspScreenLockedToaster = () => {
    return (
      <Box
        className="flex__ align__items__center pt-sm pb-sm pl-md pr-md"
        sx={{
          gap: '8px',
          backgroundColor: '#2A4241',
          borderRadius: '60px',
          height: '32px',
        }}
      >
        <Lock height={'18px'} />
        <MediumTypography
          labelid="IFSP.LockedToaster"
          defaultlabel="Locked"
          textColor="#fff"
        />
      </Box>
    );
  };
  return (
    <Box sx={{ backgroundColor: '#E4EEEE' }}>
      <ModalPopup
        open={isAnyServicesChangedWithoutIfspLockModal}
        description="IFSP.UnSavedChangesWithoutLockSoftMessage"
        labelId1="Clientpage.Yesbtn"
        negativeActionLabel="Yes"
        labelId2="Clientpage.Nobtn"
        positiveActionLabel="No"
        onCancel={() => {
          handleAddOrUpdateAfterChecks();
        }}
        onOk={() => {
          setIsAnyServicesChangedWithoutIfspLockModal(false);
        }}
      />
      <ModalPopup
        open={isInsuranceConsentMissingModal}
        description="IFSP.InsuranceConsentNotSelectedLockMessage"
        defaultDescription="Consent for the Insurance within the IFSP is not selected. Are you sure to proceed?"
        labelId1="Clientpage.cancelbtn"
        negativeActionLabel="Cancel"
        labelId2="Clientpage.Okbtn"
        positiveActionLabel="Ok"
        onCancel={() => {
          setIsInsuranceConsentMissingModal(false);
        }}
        onOk={() => {
          setIsInsuranceConsentMissingModal(false);
          setIsSaveClicked(false);
          handleAddOrUpdateAfterChecks();
        }}
      />
      <IfspUpdateModalPopup
        openIfspUpdateModal={openUpdateModal}
        onCancel={() => setOpenUpdateModal(false)}
        onOk={() => {
          dispatch(
            updateIfspSDPAndConsent({
              ...ifspSdpAndConsentDetails,
              isLocked: false,
              consent: [
                ...ifspSdpAndConsentDetails.consent,
                {
                  ...createConsent(),
                  ifspId: ifspSdpAndConsentDetails.ifspId || '',
                  consentType: 'consentForChange',
                  latestConsent: true,
                },
              ],
            }),
          );
          setOpenUpdateModal(false);
        }}
        labelId1="Clientpage.cancelbtn"
        negativeActionLabel="Cancel"
        labelId2="Clientpage.Okbtn"
        positiveActionLabel="Ok"
      />
      {ifspSdpAndConsentDetails.isCurrentlyHeld ? (
        <Box sx={{ position: 'relative' }}>
          <IfspHeld ifspHealBy={ifspSdpAndConsentDetails?.heldBy} />
        </Box>
      ) : null}

      <Box
        ref={parentRef}
        style={{
          pointerEvents: ifspSdpAndConsentDetails.isCurrentlyHeld
            ? 'none'
            : 'auto',
          opacity: ifspSdpAndConsentDetails.isCurrentlyHeld ? '0.5' : '1 ',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onKeyDownCapture={(e) => {
          if (ifspSdpAndConsentDetails.isCurrentlyHeld) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (ifspSdpAndConsentDetails.isCurrentlyHeld) {
            if (parentRef.current) parentRef.current.focus();
          }
        }}
      >
        <SnackBarComponent
          open={open}
          handleClose={handleCloseSnakBar}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
        <ModalPopup
          open={openUnSavedModal}
          description="formUnsavedChangesMessage"
          onCancel={() => {
            setOpenUnSavedModal(false);
          }}
          onOk={() => {
            dispatch(setIfspSDPAndConsent(ifspSdpAndConsentOriginalDetails));
            setIsUnsavedPopupConfirmed(true);
            setTransitionRenderKey((prev) => prev + 1);
            setTimeout(() => {
              setOpenUnSavedModal(false);
            }, 500);
          }}
          labelId1="Clientpage.cancelbtn"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Okbtn"
          positiveActionLabel="Ok"
        />
        <Box className="flex__ justifyContentSpaceBtw alignItemCenter">
          <MediumTypography
            labelid="IFSP.SDPAndConsent"
            defaultlabel="Service Delivery"
            fontSize="18px"
            fontweight={600}
            className="pb-md pl-md pt-md"
          />
          {ifspSdpAndConsentDetails.isLocked && ifspScreenLockedToaster()}
          <Box className="flex__ align__items__center pr-md">
            {ifspSdpAndConsentDetails.isLocked &&
              ifspPermission.hasClientEditPermission && (
                <ButtonComponent
                  className="btn-primary btn-submit mr-md"
                  labelId="NEWIFSP.Unlock"
                  defaultLabelId="Unlock"
                  variantType="contained"
                  onClick={() => {
                    setOpenUpdateModal(true);
                  }}
                />
              )}
            <DownloadIcon
              className="cursorPointer mr-xlg mt-xs"
              onClick={handleDownload}
            />
          </Box>
        </Box>
        {ifspSdpAndConsentDetails.ifspId && (
          <Box key={transitionRenderKey}>
            <ServiceDeliveryPlan
              isUnsavedPopupConfirmed={isUnsavedPopupConfirmed}
            />
            <IfspConsent isSaveClicked={isSaveClicked} />
          </Box>
        )}
        <Box
          component="div"
          className="flex__ justifyContent-FlexEnd pt-lg pb-lg pr-md"
        >
          <ButtonComponent
            className="btn-primary btn-cancel"
            labelId={'cancelText'}
            defaultLabelId="Cancel"
            variantType="outlined"
            onClick={() => {
              if (
                isSDPAndConsentChnaged ||
                ifspUnsavedFields.isServiceDeliveryFieldsChanged
              ) {
                setOpenUnSavedModal(true);
              } else {
                setOpenUnSavedModal(false);
              }
            }}
            disabled={!ifspPermission.hasClientEditPermission}
          />
          <ButtonComponent
            className="btn-primary btn-submit  ml-md"
            labelId="IFSP.SaveServiceDelivery"
            defaultLabelId="Save Service Delivery"
            variantType="contained"
            onClick={() => {
              handleAddOrUpdate();
            }}
            disabled={
              (ifspSdpAndConsentDetails.isLocked &&
                ((!ifspPermission.hasIfspEditPermissionAfterLock &&
                  !ifspPermission.hasAdminAmendmentEditPermission) ||
                  !ifspPermission.hasClientEditPermission)) ||
              !ifspPermission.hasClientEditPermission
            }
          />
        </Box>
      </Box>
    </Box>
  );
};
